import { EventTarget } from 'cc';
import type StreamingError from '../StreamingError';

let tempCanvas: HTMLCanvasElement;
let tempContext: CanvasRenderingContext2D;

export type ScreenshotConfig = {
    width: number;
    height: number;
    type: string;
    quality: number;
};

export enum CommonStreamingErrorName {
    UNKNOWN_ERROR = 'UNKNOWN_ERROR',
    OTHER_ERROR = '其它錯誤',
}

enum EventType {
    Play = 'play',
    Resize = 'resize',
    Error = 'error',
}

export default abstract class StreamingPlayerAdapter extends EventTarget {
    public static readonly EVENT_TYPE = EventType;

    protected readonly onResizeCallback = this._onResize.bind(this);

    public abstract readonly videoElement: HTMLVideoElement;
    public abstract get muted(): boolean;
    public abstract set muted(value: boolean);
    public abstract get volume(): number;
    public abstract set volume(value: number);
    public abstract get controls(): boolean;
    public abstract set controls(value: boolean);
    public abstract get playsinline(): boolean;
    public abstract set playsinline(value: boolean);
    public abstract get loop(): boolean;
    public abstract set loop(value: boolean);
    public abstract get height(): number;
    public abstract get width(): number;

    public emit(type: EventType.Play): void;
    public emit(type: EventType.Resize): void;
    public emit(type: EventType.Error, error: StreamingError): void;
    public emit(type: string): void;
    public emit(type: EventType | string, error?: StreamingError): void {
        super.emit(type, error);
    }

    public on(type: EventType.Play, callback: () => void, target?: any): void;
    public on(type: EventType.Resize, callback: () => void, target?: any): void;
    public on(type: EventType.Error, callback: (error: StreamingError) => void, target?: any): void;
    public on(type: string, callback: (...args: any[]) => void, target?: any): void;
    public on(type: EventType | string, callback: (...args: any[]) => void, target?: any): void {
        super.on(type, callback, target);
    }

    public snapshot(config?: Partial<ScreenshotConfig>): string {
        if (!this.videoElement) {
            return '';
        }
        const video: HTMLVideoElement = this.videoElement;
        const canvas = tempCanvas ?? (tempCanvas = document.createElement('canvas'));
        const context = tempContext ?? (tempContext = canvas.getContext('2d'));
        const request: ScreenshotConfig = {
            width: config?.width ?? video.videoWidth,
            height: config?.height ?? video.videoHeight,
            type: config?.type ?? 'image/webp',
            quality: config?.quality ?? 0.8,
        };
        canvas.width = request.width;
        canvas.height = request.height;
        context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL(request.type, request.quality);
    }

    private _onResize() {
        this.emit(EventType.Resize);
    }

    public abstract play(...args: any): void;
    public abstract resume(): Promise<void>;
    public abstract pause(): void;
    public abstract stop(): void;
}
